/**
 * Sky Preview Visualization
 *
 * Creates a real-time interactive 3D preview showing:
 * - Sky hemisphere
 * - Search cone (updates as parameters change)
 * - Target direction marker
 * - Cardinal directions
 *
 * This preview helps users understand which area of the sky they're about to search
 * BEFORE clicking "Find Objects".
 */

export class SkyPreview {
    constructor() {
        this.containerId = 'skyPreviewPlot';
        this.plotDiv = null;
        this.isInitialized = false;

        // Dark theme colors matching the Obsidian palette
        this.darkTheme = {
            paper_bgcolor: '#0a0a0a',
            plot_bgcolor: '#141414',
            text_primary: '#ffffff',
            text_secondary: '#b0b0b0',
            text_muted: '#707070',
            accent: '#6b8cae',
            border: '#2a2a2a',
            horizon: 'rgba(255, 165, 0, 0.6)',
            sphere: 'rgba(107, 140, 174, 0.15)',
            searchArea: 'rgba(255, 193, 7, 0.3)',
            searchBorder: 'rgba(255, 193, 7, 0.8)',
            observer: '#f87171',
            target: '#6b8cae'
        };
    }

    /**
     * Initialize the sky preview visualization
     */
    initialize() {
        console.log('Initializing SkyPreview...');

        this.plotDiv = document.getElementById(this.containerId);
        if (!this.plotDiv) {
            console.log('Sky preview container not found - preview disabled');
            return false;
        }

        // Check if Plotly is available
        if (typeof Plotly === 'undefined') {
            console.warn('Plotly library not found - preview disabled');
            return false;
        }

        // Create initial preview
        this.createInitialPreview();

        this.isInitialized = true;
        console.log('SkyPreview initialized successfully');
        return true;
    }

    /**
     * Create the initial preview with default parameters
     */
    createInitialPreview() {
        const defaultParams = {
            azimuth: 180,
            minAltitude: 30,
            maxAltitude: 70,
            fieldOfView: 15
        };

        this.renderPreview(defaultParams);
    }

    /**
     * Update the preview with new search parameters
     * @param {Object} params - Search parameters
     */
    update(params) {
        if (!this.isInitialized || !this.plotDiv) {
            return;
        }

        this.renderPreview(params);
    }

    /**
     * Render the sky preview
     * @param {Object} params - Search parameters
     */
    renderPreview(params) {
        const { azimuth, minAltitude, maxAltitude, fieldOfView } = params;
        const targetAltitude = (minAltitude + maxAltitude) / 2;

        const traces = [];

        // 1. Celestial hemisphere
        traces.push(this.createHemisphere());

        // 2. Horizon circle
        traces.push(this.createHorizonCircle());

        // 3. Cardinal directions
        traces.push(this.createCardinalDirections());

        // 4. Observer marker
        traces.push(this.createObserver());

        // 5. Search area (returns array of traces)
        const searchAreaTraces = this.createSearchCone(azimuth, minAltitude, maxAltitude, fieldOfView);
        traces.push(...searchAreaTraces);

        // 6. Target direction marker
        traces.push(this.createTargetMarker(azimuth, targetAltitude));

        // 7. Direction line
        traces.push(this.createDirectionLine(azimuth, targetAltitude));

        // Get current camera position if plot exists (to preserve user's view)
        let currentCamera = null;
        if (this.plotDiv.layout && this.plotDiv.layout.scene && this.plotDiv.layout.scene.camera) {
            currentCamera = this.plotDiv.layout.scene.camera;
        }

        const layout = this.createLayout(currentCamera);

        const config = {
            responsive: true,
            displayModeBar: true,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
            displaylogo: false
        };

        // Use react for efficient updates
        if (this.plotDiv.data && this.plotDiv.data.length > 0) {
            Plotly.react(this.containerId, traces, layout, config);
        } else {
            Plotly.newPlot(this.containerId, traces, layout, config);
        }
    }

    /**
     * Create hemisphere surface
     */
    createHemisphere() {
        const resolution = 30;
        const x_hemi = [];
        const y_hemi = [];
        const z_hemi = [];

        for (let i = 0; i <= resolution; i++) {
            const x_row = [];
            const y_row = [];
            const z_row = [];

            for (let j = 0; j <= resolution; j++) {
                const phi_val = (i / resolution) * Math.PI / 2;
                const theta_val = (j / resolution) * 2 * Math.PI;

                x_row.push(Math.sin(phi_val) * Math.cos(theta_val));
                y_row.push(Math.sin(phi_val) * Math.sin(theta_val));
                z_row.push(Math.cos(phi_val));
            }

            x_hemi.push(x_row);
            y_hemi.push(y_row);
            z_hemi.push(z_row);
        }

        return {
            type: 'surface',
            x: x_hemi,
            y: y_hemi,
            z: z_hemi,
            colorscale: [[0, this.darkTheme.sphere], [1, 'rgba(107, 140, 174, 0.25)']],
            showscale: false,
            hoverinfo: 'skip',
            name: 'Sky Dome',
            showlegend: false,
            lighting: {
                ambient: 0.8,
                diffuse: 0.5
            }
        };
    }

    /**
     * Create horizon circle
     */
    createHorizonCircle() {
        const points = { x: [], y: [], z: [] };
        const radius = 1;

        for (let az = 0; az <= 360; az += 5) {
            const coords = this.azAltToSphereCoords(az, 0, radius);
            points.x.push(coords.x);
            points.y.push(coords.y);
            points.z.push(coords.z);
        }

        return {
            x: points.x,
            y: points.y,
            z: points.z,
            type: 'scatter3d',
            mode: 'lines',
            line: {
                color: this.darkTheme.horizon,
                width: 4
            },
            name: 'Horizon',
            showlegend: true,
            hoverinfo: 'name'
        };
    }

    /**
     * Create cardinal direction markers
     */
    createCardinalDirections() {
        const directions = [
            { label: 'N (0°)', az: 0 },
            { label: 'E (90°)', az: 90 },
            { label: 'S (180°)', az: 180 },
            { label: 'W (270°)', az: 270 }
        ];

        const x = [];
        const y = [];
        const z = [];
        const text = [];

        directions.forEach(dir => {
            const coords = this.azAltToSphereCoords(dir.az, 0, 1.1);
            x.push(coords.x);
            y.push(coords.y);
            z.push(coords.z);
            text.push(dir.label);
        });

        return {
            type: 'scatter3d',
            mode: 'markers+text',
            x: x,
            y: y,
            z: z,
            marker: {
                size: 8,
                color: this.darkTheme.text_muted,
                symbol: 'diamond'
            },
            text: text,
            textposition: 'top center',
            textfont: {
                size: 12,
                color: this.darkTheme.text_primary
            },
            name: 'Cardinal Directions',
            showlegend: false,
            hoverinfo: 'text'
        };
    }

    /**
     * Create observer marker at center
     */
    createObserver() {
        return {
            type: 'scatter3d',
            mode: 'markers',
            x: [0],
            y: [0],
            z: [0],
            marker: {
                size: 14,
                color: this.darkTheme.observer,
                symbol: 'circle',
                line: {
                    color: this.darkTheme.text_primary,
                    width: 2
                }
            },
            name: 'Observer',
            showlegend: true,
            hoverinfo: 'name'
        };
    }

    /**
     * Create search area on sphere (bounded by min/max altitude and azimuth range)
     */
    createSearchCone(azimuth, minAltitude, maxAltitude, coneRadius) {
        const traces = [];

        // Create filled search area mesh
        const meshPoints = { x: [], y: [], z: [], i: [], j: [], k: [] };
        const steps = 20; // Resolution of the mesh

        // Generate mesh grid for the search area
        for (let altStep = 0; altStep <= steps; altStep++) {
            for (let azStep = 0; azStep <= steps; azStep++) {
                const altFrac = altStep / steps;
                const azFrac = azStep / steps;

                // Calculate position within search area
                const currentAlt = minAltitude + (maxAltitude - minAltitude) * altFrac;
                const currentAz = azimuth - coneRadius + (2 * coneRadius * azFrac);

                // Keep altitude within bounds
                const clampedAlt = Math.max(0, Math.min(90, currentAlt));
                const normalizedAz = ((currentAz % 360) + 360) % 360;

                const coords = this.azAltToSphereCoords(normalizedAz, clampedAlt, 1.005);
                meshPoints.x.push(coords.x);
                meshPoints.y.push(coords.y);
                meshPoints.z.push(coords.z);
            }
        }

        // Create triangles for the mesh
        for (let row = 0; row < steps; row++) {
            for (let col = 0; col < steps; col++) {
                const idx = row * (steps + 1) + col;
                const idx_right = idx + 1;
                const idx_bottom = idx + (steps + 1);
                const idx_bottom_right = idx_bottom + 1;

                // First triangle
                meshPoints.i.push(idx);
                meshPoints.j.push(idx_right);
                meshPoints.k.push(idx_bottom);

                // Second triangle
                meshPoints.i.push(idx_right);
                meshPoints.j.push(idx_bottom_right);
                meshPoints.k.push(idx_bottom);
            }
        }

        // Add filled mesh trace
        traces.push({
            type: 'mesh3d',
            x: meshPoints.x,
            y: meshPoints.y,
            z: meshPoints.z,
            i: meshPoints.i,
            j: meshPoints.j,
            k: meshPoints.k,
            opacity: 0.3,
            color: this.darkTheme.searchArea,
            name: 'Search Area',
            showlegend: false,
            hoverinfo: 'skip'
        });

        // Add border outlines
        const borderPoints = { top: {x: [], y: [], z: []}, bottom: {x: [], y: [], z: []},
                              left: {x: [], y: [], z: []}, right: {x: [], y: [], z: []} };
        const numBorderPoints = 36;

        // Top border (max altitude)
        for (let i = 0; i <= numBorderPoints; i++) {
            const azFrac = i / numBorderPoints;
            const currentAz = azimuth - coneRadius + (2 * coneRadius * azFrac);
            const coords = this.azAltToSphereCoords(currentAz, Math.min(90, maxAltitude), 1.01);
            borderPoints.top.x.push(coords.x);
            borderPoints.top.y.push(coords.y);
            borderPoints.top.z.push(coords.z);
        }

        // Bottom border (min altitude)
        for (let i = 0; i <= numBorderPoints; i++) {
            const azFrac = i / numBorderPoints;
            const currentAz = azimuth - coneRadius + (2 * coneRadius * azFrac);
            const coords = this.azAltToSphereCoords(currentAz, Math.max(0, minAltitude), 1.01);
            borderPoints.bottom.x.push(coords.x);
            borderPoints.bottom.y.push(coords.y);
            borderPoints.bottom.z.push(coords.z);
        }

        // Left border (azimuth - coneRadius)
        for (let i = 0; i <= numBorderPoints; i++) {
            const altFrac = i / numBorderPoints;
            const currentAlt = minAltitude + (maxAltitude - minAltitude) * altFrac;
            const coords = this.azAltToSphereCoords(azimuth - coneRadius, currentAlt, 1.01);
            borderPoints.left.x.push(coords.x);
            borderPoints.left.y.push(coords.y);
            borderPoints.left.z.push(coords.z);
        }

        // Right border (azimuth + coneRadius)
        for (let i = 0; i <= numBorderPoints; i++) {
            const altFrac = i / numBorderPoints;
            const currentAlt = minAltitude + (maxAltitude - minAltitude) * altFrac;
            const coords = this.azAltToSphereCoords(azimuth + coneRadius, currentAlt, 1.01);
            borderPoints.right.x.push(coords.x);
            borderPoints.right.y.push(coords.y);
            borderPoints.right.z.push(coords.z);
        }

        // Add border traces
        traces.push({
            x: borderPoints.top.x,
            y: borderPoints.top.y,
            z: borderPoints.top.z,
            type: 'scatter3d',
            mode: 'lines',
            line: { color: this.darkTheme.searchBorder, width: 4 },
            name: 'Search Area',
            showlegend: true,
            hovertemplate: `Search Area<br>Center: ${azimuth}° Az<br>Alt: ${minAltitude}° - ${maxAltitude}°<br>FOV: ±${coneRadius}°<extra></extra>`
        });

        traces.push({
            x: borderPoints.bottom.x,
            y: borderPoints.bottom.y,
            z: borderPoints.bottom.z,
            type: 'scatter3d',
            mode: 'lines',
            line: { color: this.darkTheme.searchBorder, width: 4 },
            showlegend: false,
            hoverinfo: 'skip'
        });

        traces.push({
            x: borderPoints.left.x,
            y: borderPoints.left.y,
            z: borderPoints.left.z,
            type: 'scatter3d',
            mode: 'lines',
            line: { color: this.darkTheme.searchBorder, width: 4 },
            showlegend: false,
            hoverinfo: 'skip'
        });

        traces.push({
            x: borderPoints.right.x,
            y: borderPoints.right.y,
            z: borderPoints.right.z,
            type: 'scatter3d',
            mode: 'lines',
            line: { color: this.darkTheme.searchBorder, width: 4 },
            showlegend: false,
            hoverinfo: 'skip'
        });

        return traces;
    }

    /**
     * Create target direction marker
     */
    createTargetMarker(azimuth, altitude) {
        const coords = this.azAltToSphereCoords(azimuth, altitude, 1);

        return {
            type: 'scatter3d',
            mode: 'markers',
            x: [coords.x],
            y: [coords.y],
            z: [coords.z],
            marker: {
                size: 12,
                color: this.darkTheme.target,
                symbol: 'diamond',
                line: {
                    color: this.darkTheme.text_primary,
                    width: 2
                }
            },
            name: `Target: ${azimuth}° az, ${altitude.toFixed(0)}° alt`,
            showlegend: true,
            hoverinfo: 'name'
        };
    }

    /**
     * Create direction line from observer to target
     */
    createDirectionLine(azimuth, altitude) {
        const coords = this.azAltToSphereCoords(azimuth, altitude, 1);

        return {
            type: 'scatter3d',
            mode: 'lines',
            x: [0, coords.x],
            y: [0, coords.y],
            z: [0, coords.z],
            line: {
                color: this.darkTheme.target,
                width: 4,
                dash: 'dash'
            },
            name: 'View Direction',
            showlegend: false,
            hoverinfo: 'skip'
        };
    }

    /**
     * Convert azimuth/altitude to 3D sphere coordinates
     */
    azAltToSphereCoords(azimuth, altitude, radius = 1) {
        const azRad = azimuth * Math.PI / 180;
        const altRad = altitude * Math.PI / 180;

        return {
            x: radius * Math.cos(altRad) * Math.sin(azRad),
            y: radius * Math.cos(altRad) * Math.cos(azRad),
            z: radius * Math.sin(altRad)
        };
    }

    /**
     * Create layout for the preview
     * @param {Object} currentCamera - Current camera position to preserve user's view (optional)
     */
    createLayout(currentCamera = null) {
        // Use preserved camera position if available, otherwise use default
        const camera = currentCamera || {
            eye: { x: 1.8, y: -1.2, z: 0.6 }
        };

        return {
            scene: {
                aspectmode: 'data',
                camera: camera,
                xaxis: { visible: false },
                yaxis: { visible: false },
                zaxis: { visible: false },
                domain: { x: [0, 1], y: [0, 1] },
                bgcolor: this.darkTheme.plot_bgcolor
            },
            showlegend: true,
            legend: {
                x: 0.02,
                y: 0.98,
                bgcolor: 'rgba(10, 10, 10, 0.9)',
                bordercolor: this.darkTheme.border,
                borderwidth: 1,
                font: { color: this.darkTheme.text_secondary }
            },
            autosize: true,
            margin: { l: 0, r: 0, t: 10, b: 0 },
            paper_bgcolor: this.darkTheme.paper_bgcolor,
            plot_bgcolor: this.darkTheme.plot_bgcolor,
            font: { color: this.darkTheme.text_secondary, family: 'Inter, sans-serif' }
        };
    }

    /**
     * Clear the preview
     */
    clear() {
        if (this.plotDiv && typeof Plotly !== 'undefined') {
            Plotly.purge(this.plotDiv);
        }
        this.isInitialized = false;
    }
}

export default SkyPreview;
